import React, { useEffect, useState } from 'react';
import { OperationListItemStyle } from '../OperationListView.styles';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { useStowageStore } from 'src/context/StowageContext';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { isLoadingOrUnloadingCargoOperation } from 'src/utils/stowage/cargoOperationUtils';
import { StowageRuleConsequenceType } from 'src/enums/stowage/stowageRule/stowageRuleConsequenceType';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { getCargoBerthViolationStatus, getCargoPortViolationStatus } from 'src/utils/stowage/violations/violationsUtils';
import { useVesselStore } from 'src/context/VesselContext';
import { useRotationStore } from 'src/context/RotationContext';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import { useAuthStore } from 'src/context/AuthContext';
import { createCustomDragImage } from './createCustomDragImage';
import OperationStatusColumn from './OperationStatusColumn';
import OperationColumnsContainer from './OperationColumnsContainer';
import { useCargoOperationDragDrop } from '../hooks/useCargoOperationDragDrop';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
// Extracted utility imports
import {
    getOperationStowageStatus,
    getSelectedOperationListItemClass,
    calculateMenuYPosition,
} from 'src/utils/stowage/operationListView/operationListItemUtils';
import { getUnavailableTanks, getUnavailableTanksForUnloading } from 'src/utils/stowage/operationListView/tankAvailabilityUtils';

type Props = {
    cargoOperation: RotationCargoOperation;
};

const OperationListItemComponent: React.FC<Props> = ({ cargoOperation }) => {
    const { selectedVessel } = useVesselStore();
    const {
        activeStowagePlan,
        viewCargoOperation,
        setViewCargoOperation,
        operationListContextMenuState,
        setOperationListContextMenuState,
        tankPlanWindowResized,
        setUnavailableTanks,
        setUnavailableTankViolations,
        tankPlan,
        operationListColumnWidths,
        orderedColumns,
        popOutOperationList,
        portalRootHeight,
    } = useStowageStore();
    const { coatingCompatibilities, uscgCompatibilities, selectedStowageRotationPortCall, selectedStowageRotationBerthCall } = useRotationStore();
    const { selectedCargoOperation, setSelectedCargoOperation, selectedTanks, setSelectedTab, setSelectedViolationListFilter } =
        useStowageSelectionStore();
    const { violations } = useViolationsStore();
    const { role } = useAuthStore();
    const [violationType, setViolationType] = useState<StowageRuleConsequenceType | null>(null);
    const [cargoOperationSelectedClass, setCargoOperationSelectedClass] = useState('');
    const { handleDrop } = useCargoOperationDragDrop();
    const { setQueryParamStowageCargoOperation } = useStowageQueryParamsUtils();

    // Effect hooks
    useEffect(() => {
        if (!activeStowagePlan || !violations) return;

        if (selectedStowageRotationBerthCall) {
            const _violationType = getCargoBerthViolationStatus(cargoOperation.cargoGuid, selectedStowageRotationBerthCall.berthCallGuid, violations);
            setViolationType(_violationType);
        } else if (selectedStowageRotationPortCall) {
            const _violationType = getCargoPortViolationStatus(cargoOperation.cargoGuid, selectedStowageRotationPortCall.portCallGuid, violations);
            setViolationType(_violationType);
        }
    }, [activeStowagePlan, violations]);

    useEffect(() => {
        if (!activeStowagePlan) return;
        setCargoOperationSelectedClass(
            getSelectedOperationListItemClass({
                selectedCargoOperation,
                activeStowagePlan,
                selectedTanks,
                selectedStowageRotationPortCall,
                cargoOperation,
            })
        );
    }, [activeStowagePlan, selectedCargoOperation, selectedTanks]);

    // Operation handlers
    const handleCargoOperationSelection = () => {
        setQueryParamStowageCargoOperation(
            cargoOperation.portCallGuid,
            cargoOperation.cargoOperationGuid,
            selectedStowageRotationBerthCall ? cargoOperation.berthCallGuid : null
        );
    };

    const handleViewCargoOperation = () => {
        if (cargoOperation.cargoGuid !== viewCargoOperation?.cargo?.cargoGuid) {
            setViewCargoOperation(cargoOperation);
        } else {
            setViewCargoOperation(null);
        }
    };

    const handleViewViolations = () => {
        setSelectedTab('Violation List');
        setSelectedViolationListFilter('Selected cargo');
    };

    // Drag and drop handlers
    const handleDragStart = (e: React.DragEvent) => {
        setSelectedCargoOperation(cargoOperation);
        e.dataTransfer.setData('dragOperationType', cargoOperation.operationType);
        e.dataTransfer.setData('cargoOperationGuid', cargoOperation.cargoOperationGuid);

        // Create custom drag image
        createCustomDragImage(cargoOperation.cargo, cargoOperation.operationType, e);

        if (cargoOperation.operationType === 'Loading') {
            const result = getUnavailableTanks({
                activeStowagePlan,
                cargoOperation,
                tankPlan,
                selectedVessel,
                violations,
                coatingCompatibilities,
                uscgCompatibilities,
            });
            setUnavailableTanks(result.tanks);
            setUnavailableTankViolations(result.tankViolations);
        } else if (cargoOperation.operationType === 'Unloading') {
            setUnavailableTanks(
                getUnavailableTanksForUnloading({
                    activeStowagePlan,
                    cargoOperation,
                    tankPlan,
                })
            );
            setUnavailableTankViolations(null);
        }
    };

    const handleDragEnd = () => {
        setUnavailableTanks(null);
        setUnavailableTankViolations(null);
        // Reset any modified cursors
        document.body.style.cursor = 'default';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onDrop = (e) => {
        handleDrop(e, cargoOperation.berthCall, cargoOperation.berthCall.portCall, cargoOperation);
    };

    // Menu handlers
    const handleOpenOperationMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const menuYPosition = calculateMenuYPosition({
            clientY: e.clientY,
            windowHeight: window.innerHeight,
            windowWidth: window.innerWidth,
            tankPlanWindowResized,
            popOutOperationList,
            portalRootHeight,
        });
        setOperationListContextMenuState({
            visible: !operationListContextMenuState.visible,
            tank: null,
            cargoContents: [],
            cargoOperationGuid: cargoOperation.cargoOperationGuid,
            berthCall: null,
            x: 35,
            y: menuYPosition,
            violations: null,
        });

        handleCargoOperationSelection();
    };

    // Get status icon column configuration
    const statusIconColumn = orderedColumns.find((col) => col.id === 'statusIcon');

    // Calculate stowage status for status column
    const stowageStatus =
        activeStowagePlan && isLoadingOrUnloadingCargoOperation(cargoOperation) ? getOperationStowageStatus(activeStowagePlan, cargoOperation) : null;

    return (
        orderedColumns.length > 0 && (
            <OperationListItemStyle
                onClick={handleCargoOperationSelection}
                onContextMenu={handleOpenOperationMenu}
                className={`operation-list-item ${cargoOperationSelectedClass}`}
                onDragOver={handleDragOver}
            >
                <div
                    className="operation-list-item-content"
                    draggable={hasPermission(role, stowageFeatureEnum.EditStowage) && isLoadingOrUnloadingCargoOperation(cargoOperation)}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => onDrop(e)}
                >
                    {statusIconColumn && (
                        <OperationStatusColumn
                            cargoOperation={cargoOperation}
                            violationType={violationType}
                            columnWidth={operationListColumnWidths['statusIcon']}
                            stowageStatus={stowageStatus}
                            onOpenOperationMenu={handleOpenOperationMenu}
                            onViewCargoOperation={handleViewCargoOperation}
                            onViewViolations={handleViewViolations}
                        />
                    )}
                    <OperationColumnsContainer
                        orderedColumns={orderedColumns}
                        cargoOperation={cargoOperation}
                        activeStowagePlan={activeStowagePlan}
                        operationListColumnWidths={operationListColumnWidths}
                    />
                </div>
            </OperationListItemStyle>
        )
    );
};

export default OperationListItemComponent;
